import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { ID } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  pipeState,
} from '@core/ngrx-legacy';
import type { TalentPoolData, TalentPoolFull } from '@core/talent-pools-types';

import { TalentPoolsScope } from '../types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TalentPoolFull>()(
  'TalentPoolsFull',
  {
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, ({ id }) => id!);
      return {
        fetchOne: (state, payload: { id: number | string }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        fetchOneSuccess: (state, payload: { id: number | string; talentPool: TalentPoolFull }) =>
          pipeState(
            state,
            next => adapter.replaceOne(next, payload.id, payload.talentPool),
            next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
          ),
        fetchOneFailed: (state, payload: { id: number | string; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),

        follow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        followSuccess: actions.updateOnePessimisticSuccess,
        followFailed: actions.updateOnePessimisticFailed,

        unfollow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        unfollowSuccess: actions.updateOnePessimisticSuccess,
        unfollowFailed: actions.updateOnePessimisticFailed,

        duplicate: (
          state,
          payload: {
            id: number;
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: TalentPoolFull }) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload.data),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: actions.createOnePessimisticFailed,

        archive: (state, payload: { id: ID }) => adapter.markAsPending(state),
        archiveSuccess: (state, payload: { data: TalentPoolFull }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: TalentPoolsScope.Archived,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        archiveFailed: actions.updateOnePessimisticFailed,

        retrieve: (state, payload: { id: ID }) => adapter.markAsPending(state),
        retrieveSuccess: (state, payload: { data: TalentPoolFull }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: TalentPoolsScope.Default,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        retrieveFailed: actions.updateOnePessimisticFailed,

        createOnePessimistic: (state, payload: { data: TalentPoolData }) =>
          adapter.markAsPending(state),

        updateOnePessimistic: (state, payload: { data: TalentPoolData & { id: number } }) =>
          adapter.markAsPending(state),
        updateOnePessimisticSuccess: (state, payload: { data: TalentPoolFull }) =>
          actions.updateOnePessimisticSuccess(state, payload),
        updateOnePessimisticFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
      };
    },
  },
);

const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(selectors.allEntities, (state: TalentPoolFull[]) =>
    state.find(offer => offer.slug === identifier || offer.id === identifier),
  ),
);

const metaByIdOrSlug = memoizeSelector((identifier: number | string) => selectors.meta(identifier));

const TalentPoolsFullSelectors = {
  ...selectors,
  byIdOrSlug,
  metaByIdOrSlug,
};

export {
  TalentPoolsFullSelectors,
  actions as TalentPoolsFullActions,
  reducer as TalentPoolsFullReducer,
  namespace as TalentPoolsFullNamespace,
};
